import { useState } from 'react';
import { MethodApiId } from '../../../data/methods';
import { useCurrencySubmission } from '../../../hooks';
import {
  ApiChainSymbols,
  ApiCurrencySymbols,
  binancePayAsBlockchain,
  binancePayDefaultCurrency,
} from '../../../utils/enums';
import { usePrefetchPaymentRoots } from '../../Payment/hooks/prefetchPaymentRoots';
import { Currency } from '../CurrencySelection';
import { getPageTypeFromBlockchains } from '../utils';
import { PaymentMethod } from '../../../graphql/customTypes';

interface UseCurrencySelectionProps {
  onSelectionError: () => void;
  onSolanaPaySelected?: () => void;
  currencies: Currency[];
  selectedMethod: PaymentMethod;
}

export const useCurrencySelection = ({
  onSelectionError,
  onSolanaPaySelected,
  currencies,
  selectedMethod,
}: UseCurrencySelectionProps) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [isNetworkSelectionOpen, setIsNetworkSelectionOpen] = useState(false);
  const { loading, submitCurrencySelection } = useCurrencySubmission({
    onSelectionError,
  });
  const prefetch = usePrefetchPaymentRoots();

  const handleCurrencySelection = (currencySymbol: string) => {
    setSelectedCurrency(currencySymbol);
    setIsNetworkSelectionOpen(true);

    const currency = currencies.find((c) => c.symbol === currencySymbol);
    const pageType = getPageTypeFromBlockchains(currency);

    if (pageType) {
      // setTimeout prevents bottomsheet animation performance issues
      setTimeout(() => {
        prefetch(pageType);
      }, 250);
    }
  };

  const handleNetworkSelection = (network: string) => {
    if (
      network === ApiChainSymbols.SOL &&
      selectedCurrency === ApiCurrencySymbols.USDC
    ) {
      setIsNetworkSelectionOpen(false);
      onSolanaPaySelected?.();
      return;
    }

    selectedCurrency &&
      handleSelectionComplete({
        currency: selectedCurrency,
        network,
        methodId: MethodApiId.ExchangeAnother,
        paymentMethod: selectedMethod,
      });
  };

  const handleBinancePaySelection = async () => {
    setSelectedCurrency(binancePayDefaultCurrency);
    await handleSelectionComplete({
      currency: binancePayDefaultCurrency,
      network: binancePayAsBlockchain,
      methodId: MethodApiId.BinancePay,
      paymentMethod: PaymentMethod.EXCHANGE,
    });
  };

  const closeNetworkSelection = () => {
    setIsNetworkSelectionOpen(false);
  };

  const handleSelectionComplete = async ({
    currency,
    network,
    methodId,
    paymentMethod,
  }: {
    currency: string;
    network: string;
    methodId: MethodApiId;
    paymentMethod: PaymentMethod;
  }) => {
    await submitCurrencySelection({
      currency,
      network,
      methodId,
      paymentMethod,
    });
  };

  return {
    selectedCurrency,
    isNetworkSelectionOpen,
    loading,
    handleBinancePaySelection,
    handleCurrencySelection,
    closeNetworkSelection,
    handleNetworkSelection,
  };
};
